import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { GaPageViewTracker } from './components/GaPageViewTracker';
import { ApolloProvider } from '@apollo/client';
import { client } from './apollo/client';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { SiteConfigProvider } from './contexts/SiteConfigContext';
import { useDefaultDocumentTitle } from './hooks/useDocumentTitle';
import { useAffiliateTracking } from './hooks/useAffiliateTracking';
import FeatureFlag from './components/FeatureFlag';
import ComingSoon from './components/ComingSoon';
import {
  HomePage,
  MeetingsPage,
  RacePage,
  ResultsPage,
  TipsPage,
  DailyTipsPage,
  HighProbabilityPage,
  TimezoneTestPage,
  LoginPage,
  RegisterPage,
  VerifyPhonePage,
  VerifyEmailPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ProfilePage,
  ProfileSubscriptionsPage,
  PricingPage,
  TermsPage,
  PrivacyPage,
  ContactPage,
  UnsubscribePage,
  NotFoundPage,
  BuyPage,
  PreviewPage,
  PreviewResultsPage,
  PreviewPicksPage,
  FreeTrialPage,
  RedeemPage
} from './pages';
import PromoPage from './pages/PromoPage';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { CanonicalLink, DatePathOr } from '@shared/ui/seo/seo';
import { CheckoutIntentResumer } from '@shared/ui/checkout/CheckoutIntentResumer';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionRoute from './components/SubscriptionRoute';

// Lazy: the chart library only loads on this page
const TrackStatsPage = lazy(() => import('./pages/TrackStatsPage'));
const MultisPage = lazy(() => import('./pages/MultisPage'));
const QuaddiesPage = lazy(() => import('./pages/QuaddiesPage'));
const MultiBuildPage = lazy(() => import('./pages/MultiBuildPage'));
const MyMultisPage = lazy(() => import('./pages/MyMultisPage'));

// Component to handle document title setting
const DocumentTitleManager: React.FC = () => {
  useDefaultDocumentTitle();
  return null;
};

// Component to handle affiliate tracking within Router context
const AffiliateTracker: React.FC = () => {
  const { affiliateCode } = useAffiliateTracking();
  
  // Log affiliate code for debugging
  useEffect(() => {
    if (affiliateCode) {
      console.log(`🔗 App: Active affiliate code: ${affiliateCode}`);
    }
  }, [affiliateCode]);
  
  return null;
};

// Wrapper component to handle tips page access based on date
const TipsPageWrapper: React.FC = () => {
  const { date } = useParams<{ date?: string }>();
  
  // Check if date is within preview window (yesterday to 14 days ago)
  const isPreviewDate = (dateString?: string): boolean => {
    if (!dateString || dateString.length !== 8) return false;
    
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1;
    const day = parseInt(dateString.substring(6, 8));
    const checkDate = new Date(year, month, day);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const fourteenDaysAgo = new Date(today);
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    
    return checkDate <= yesterday && checkDate >= fourteenDaysAgo;
  };
  
  // If it's a preview date, allow public access
  if (isPreviewDate(date)) {
    return <TipsPage />;
  }
  
  // Otherwise, require authentication
  return <ProtectedRoute><TipsPage /></ProtectedRoute>;
};

const App: React.FC = () => {
  return (
    <ApolloProvider client={client}>
      <SiteConfigProvider>
        <DocumentTitleManager />
        <FeatureFlagProvider>
          <BrowserRouter>
            <GaPageViewTracker />
            <CanonicalLink />
            <AffiliateTracker />
            <CheckoutIntentResumer />
            <Routes>
          {/* Home page - today */}
          <Route path="/" element={<HomePage />} />
          
          {/* Promo page - Eagle Farm special offers */}
          <Route path="/promo" element={<PromoPage />} />
          
          {/* Home page - specific date */}
          {/* Email unsubscribe link (public; the token identifies the customer) - declared before the /:dateString catch-all */}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />

          <Route path="/:dateString" element={<DatePathOr notFound={<NotFoundPage />}><HomePage /></DatePathOr>} />
          
          {/* Results pages */}
          <Route path="/:dateString/results/:trackName" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
          <Route path="/:dateString/results/:trackName/:raceNumber" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
          <Route path="/:state/:track/:date/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
          
          {/* Tips pages - Allow public access for historical dates (preview mode) */}
          <Route path="/:state/:track/:date" element={<TipsPageWrapper />} />
          <Route path="/:dateString/tips/:trackName" element={<ProtectedRoute><RacePage /></ProtectedRoute>} />
          <Route path="/:dateString/tips/:trackName/:raceNumber" element={<ProtectedRoute><RacePage /></ProtectedRoute>} />
          
          {/* Meetings routes */}
          <Route path="/meetings" element={<ProtectedRoute><MeetingsPage /></ProtectedRoute>} />
          <Route path="/meetings/:dateString" element={<ProtectedRoute><MeetingsPage /></ProtectedRoute>} />

          {/* Daily Tips - Subscriber only */}
          <Route path="/daily-tips" element={<SubscriptionRoute><DailyTipsPage /></SubscriptionRoute>} />

          {/* High Probability - Subscriber only */}
          <Route path="/high-probability" element={<ProtectedRoute><HighProbabilityPage /></ProtectedRoute>} />
          <Route path="/tracks" element={<ProtectedRoute><Suspense fallback={null}><TrackStatsPage /></Suspense></ProtectedRoute>} />
          <Route path="/tracks/:trackCode" element={<ProtectedRoute><Suspense fallback={null}><TrackStatsPage /></Suspense></ProtectedRoute>} />
          <Route path="/multis" element={<ProtectedRoute><Suspense fallback={null}><MultisPage /></Suspense></ProtectedRoute>} />
          <Route path="/multis/quaddies" element={<ProtectedRoute><Suspense fallback={null}><QuaddiesPage /></Suspense></ProtectedRoute>} />
          <Route path="/multis/build" element={<ProtectedRoute><Suspense fallback={null}><MultiBuildPage /></Suspense></ProtectedRoute>} />
          <Route path="/multis/mine" element={<ProtectedRoute><Suspense fallback={null}><MyMultisPage /></Suspense></ProtectedRoute>} />

          {/* Legacy routes for backward compatibility */}
          <Route path="/races/:raceId" element={<ProtectedRoute><RacePage /></ProtectedRoute>} />
          <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
          
          {/* Preview routes - Historical tips (public access) */}
          <Route path="/preview" element={<PreviewPage />} />
          <Route path="/preview/:dateString" element={<PreviewPage />} />
          <Route path="/preview/picks" element={<PreviewPicksPage />} />
          <Route path="/preview/picks/:dateString" element={<PreviewPicksPage />} />
          <Route path="/preview/:state/:track/:date" element={<TipsPage />} />
          <Route path="/preview/:dateString/:trackName/results" element={<PreviewResultsPage />} />
          
          {/* Legal and Contact pages */}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Free trial landing page — public, no auth required */}
          <Route path="/free-trial" element={<FreeTrialPage />} />
          <Route path="/redeem" element={<RedeemPage />} />

          {/* Authentication pages */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Registration routes with feature flags */}
          <Route path="/register" element={
            <FeatureFlag 
              feature="enableRegistration"
              fallback={<ComingSoon 
                featureName="Registration" 
                message="Registration is coming soon. We're currently finalizing our systems to provide you with the best experience."
              />}
            >
              <RegisterPage />
            </FeatureFlag>
          } />
          
          <Route path="/verify-phone" element={
            <FeatureFlag 
              feature="enableRegistration"
              fallback={<Navigate to="/" replace />}
            >
              <VerifyPhonePage />
            </FeatureFlag>
          } />
          
          <Route path="/verify-email" element={
            <FeatureFlag 
              feature="enableRegistration"
              fallback={<Navigate to="/" replace />}
            >
              <VerifyEmailPage />
            </FeatureFlag>
          } />
          
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected pages */}
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/subscriptions" element={<ProtectedRoute><ProfileSubscriptionsPage /></ProtectedRoute>} />
          
          {/* Pricing page - Public access */}
          <Route path="/pricing" element={<PricingPage />} />
          {/* Direct checkout link for emails and ads (public; sign-up then continues to Stripe) */}
          <Route path="/buy/:packageCode" element={<BuyPage />} />


          {/* Payment pages */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          {/* Addresses that were never pages but are still linked: Stripe Checkout's cancel_url
              (payment.resolver.ts) and older upsell links. They used to fall through to the
              home page; send them to the plans rather than to "Page not found". */}
          <Route path="/payment/cancel" element={<Navigate to="/pricing" replace />} />
          <Route path="/subscription" element={<Navigate to="/pricing" replace />} />
          <Route path="/subscriptions" element={<Navigate to="/pricing" replace />} />

          {/* Test pages */}
          <Route path="/test/timezone" element={<TimezoneTestPage />} />
          
          {/* Anything else is a not-found page (noindex), not a silent redirect home */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
        </FeatureFlagProvider>
      </SiteConfigProvider>
    </ApolloProvider>
  );
};

export default App;
